/**
 * TUVI - Hacer compra (Carrito de Compras General)
 *
 * Desde un proveedor del carrito: se eligen los ítems pendientes, de qué proyectos y
 * cuántos, el valor unitario y los datos de la factura (igual que una entrada de
 * bodega). Adjuntos de toda la compra o por ítem. Queda "en gestión" en la pestaña
 * Compras; si se marca, crea la factura pendiente en Gestión de Pagos.
 */
import React, { useState, useEffect, useMemo } from 'react';
import client from '../../services/graphqlClient';
import { useNotification } from '../../context/NotificationContext';
import { subirArchivoCompra, adjuntarAFactura, money, hoyISO } from './comprasArchivos';

const num = (v) => Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 2 });
const claveLinea = (l) => `${l.source}:${l.id}`;
const claveItem = (l) => `${l.codigo}|${l.unidad}`;
const proyectoApto = (l) => `${l.proyecto_nombre || ''}${l.apartamento ? ' ' + l.apartamento : ''}`.trim();

export default function ModalHacerCompra({ titulo, lineas, onClose, onGuardado }) {
  const { success, error, warning } = useNotification();
  const [proveedores, setProveedores] = useState([]);
  const [provTexto, setProvTexto] = useState('');
  const [sel, setSel] = useState({});          // claveLinea → { on, cantidad }
  const [vu, setVu] = useState({});            // claveItem → valor unitario
  const [abiertos, setAbiertos] = useState({}); // claveItem → bool
  const [archItem, setArchItem] = useState({}); // claveItem → File[]
  const [archGen, setArchGen] = useState([]);
  const [form, setForm] = useState({ numero_factura: '', fecha_compra: hoyISO(), fecha_vencimiento: '', porcentaje_iva: '0', observaciones: '', crear_factura: true });
  const [guardando, setGuardando] = useState(false);

  // Proveedor por defecto: el de la matriz (se puede cambiar si se compra en otra parte)
  const provDefecto = lineas.find(l => l.proveedor_id) || null;
  useEffect(() => {
    client.query('{ proveedores { proveedores { id nombre nit } } }')
      .then(d => setProveedores(d?.proveedores?.proveedores || []))
      .catch(() => setProveedores([]));
    if (provDefecto) setProvTexto(provDefecto.proveedor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const provSel = proveedores.find(p => p.nombre === provTexto.trim()) || null;

  const items = useMemo(() => {
    const m = new Map();
    lineas.forEach(l => {
      const k = claveItem(l);
      if (!m.has(k)) m.set(k, { k, codigo: l.codigo, descripcion: l.descripcion, unidad: l.unidad, lineas: [] });
      m.get(k).lineas.push(l);
    });
    return [...m.values()]
      .map(it => ({ ...it, lineas: it.lineas.sort((a, b) => proyectoApto(a).localeCompare(proyectoApto(b), 'es', { numeric: true })) }))
      .sort((a, b) => String(a.codigo).localeCompare(String(b.codigo)));
  }, [lineas]);

  const pendienteDe = (l) => Number(l.cantidad_pendiente ?? l.cantidad ?? 0);
  const estado = (l) => sel[claveLinea(l)] || { on: false, cantidad: String(pendienteDe(l)) };
  const cantidadSel = (l) => (estado(l).on ? Number(estado(l).cantidad) || 0 : 0);
  const setLinea = (l, cambios) => setSel(p => ({ ...p, [claveLinea(l)]: { ...estado(l), ...cambios } }));
  const marcarItem = (it, on) => setSel(p => {
    const n = { ...p };
    it.lineas.forEach(l => { n[claveLinea(l)] = { ...(p[claveLinea(l)] || { cantidad: String(pendienteDe(l)) }), on }; });
    return n;
  });
  const marcarTodo = (on) => items.forEach(it => marcarItem(it, on));

  const resumenItem = (it) => {
    const cant = it.lineas.reduce((s, l) => s + cantidadSel(l), 0);
    const nSel = it.lineas.filter(l => estado(l).on).length;
    return { cant, nSel, subtotal: cant * (Number(vu[it.k]) || 0) };
  };
  const subtotal = items.reduce((s, it) => s + resumenItem(it).subtotal, 0);
  const iva = Math.round(subtotal * (Number(form.porcentaje_iva) || 0)) / 100;
  const total = subtotal + iva;
  const nLineas = lineas.filter(l => estado(l).on && cantidadSel(l) > 0).length;
  const todoMarcado = lineas.length > 0 && lineas.every(l => estado(l).on);

  const guardar = async () => {
    if (!provSel) return warning('Elige el proveedor de la lista');
    const elegidas = lineas.filter(l => estado(l).on && cantidadSel(l) > 0);
    if (!elegidas.length) return warning('Selecciona al menos un proyecto con cantidad mayor a 0');
    const sinValor = items.filter(it => resumenItem(it).nSel > 0 && !(Number(vu[it.k]) > 0));
    if (sinValor.length) return warning(`Ingresa el valor unitario de: ${sinValor.map(i => i.codigo).join(', ')}`);
    const archivosTotales = archGen.length + items.reduce((s, it) => s + (resumenItem(it).nSel ? (archItem[it.k] || []).length : 0), 0);
    if (form.crear_factura) {
      if (!form.numero_factura.trim()) return warning('Ingresa el número de factura');
      if (!form.fecha_vencimiento) return warning('Ingresa la fecha de vencimiento');
      if (!archivosTotales) return warning('Adjunta al menos un documento de la compra');
    }

    setGuardando(true);
    try {
      const itemsInput = elegidas.map(l => ({
        cotizacion_id: l.cotizacion_id, source: l.source, linea_id: l.id,
        cantidad: cantidadSel(l), valor_unitario: Number(vu[claveItem(l)]) || 0,
      }));
      // Subir adjuntos: los de un ítem quedan en su primera línea elegida
      const adjuntos = [];
      for (const f of archGen) adjuntos.push(await subirArchivoCompra(f));
      for (const it of items) {
        const idx = elegidas.findIndex(l => claveItem(l) === it.k);
        if (idx < 0) continue;
        for (const f of archItem[it.k] || []) adjuntos.push({ ...(await subirArchivoCompra(f)), item_index: idx });
      }
      const r = await client.mutate(`mutation($input: RegistrarCompraCarritoInput!) { registrarCompraCarrito(input: $input) { success message compra_id factura_id } }`, {
        input: {
          proveedor_id: Number(provSel.id), numero_factura: form.numero_factura.trim() || null,
          fecha_compra: form.fecha_compra || null, fecha_vencimiento: form.fecha_vencimiento || null,
          porcentaje_iva: Number(form.porcentaje_iva) || 0, crear_factura: form.crear_factura,
          observaciones: form.observaciones || null, items: itemsInput, adjuntos,
        },
      });
      const res = r.registrarCompraCarrito;
      if (!res.success) { error(res.message); return; }
      if (res.factura_id) {
        const soporte = archGen[0] || items.map(it => (archItem[it.k] || [])[0]).find(Boolean);
        if (soporte) await adjuntarAFactura(res.factura_id, soporte).catch(() => warning('La factura se creó, pero no se pudo copiar el adjunto a Gestión de Pagos'));
      }
      success(res.message);
      onGuardado(res.compra_id);
    } catch (e) {
      error(e.message || 'No se pudo registrar la compra');
    } finally {
      setGuardando(false);
    }
  };

  const archivosInput = (lista, onChange, multiple = true) => (
    <span className="cmp-archivos">
      <label className="cmp-btn-adjuntar">
        📎 Adjuntar
        <input type="file" multiple={multiple} onChange={e => { onChange([...lista, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
      </label>
      {lista.map((f, i) => (
        <span key={i} className="cmp-archivo-chip" title={f.name}>
          {f.name.length > 28 ? f.name.slice(0, 25) + '…' : f.name}
          <button type="button" onClick={() => onChange(lista.filter((_, j) => j !== i))}>×</button>
        </span>
      ))}
    </span>
  );

  return (
    <div className="cmp-overlay" onClick={() => !guardando && onClose()}>
      <div className="cmp-modal" onClick={e => e.stopPropagation()}>
        <div className="cmp-modal-header">
          <div>
            <h2>Hacer compra</h2>
            <p>{titulo} · {lineas.length} línea{lineas.length === 1 ? '' : 's'} pendiente{lineas.length === 1 ? '' : 's'}</p>
          </div>
          <button className="cmp-cerrar" onClick={onClose} disabled={guardando}>×</button>
        </div>

        <div className="cmp-modal-body">
          {/* Ítems */}
          <div className="cmp-items-toolbar">
            <label className="cmp-check-label">
              <input type="checkbox" checked={todoMarcado} onChange={e => marcarTodo(e.target.checked)} /> Seleccionar todo (todas las cantidades y proyectos)
            </label>
          </div>
          <div className="cmp-items">
            {items.map(it => {
              const r = resumenItem(it);
              const todos = it.lineas.every(l => estado(l).on);
              const abierto = !!abiertos[it.k];
              return (
                <div key={it.k} className={`cmp-item ${r.nSel ? 'activo' : ''}`}>
                  <div className="cmp-item-header">
                    <input type="checkbox" checked={todos} ref={el => { if (el) el.indeterminate = !todos && r.nSel > 0; }}
                      onChange={e => marcarItem(it, e.target.checked)} title="Todos los proyectos de este ítem" />
                    <button type="button" className="cmp-item-toggle" onClick={() => setAbiertos(p => ({ ...p, [it.k]: !abierto }))}>
                      {abierto ? '▾' : '▸'}
                    </button>
                    <div className="cmp-item-info" onClick={() => setAbiertos(p => ({ ...p, [it.k]: !abierto }))}>
                      <span className="cmp-codigo">{it.codigo}</span>
                      <span className="cmp-desc">{it.descripcion}</span>
                      <span className="cmp-sub">{r.nSel}/{it.lineas.length} proyectos · {num(r.cant)} {it.unidad}</span>
                    </div>
                    <label className="cmp-vu">
                      Valor unit.
                      <input type="number" min="0" placeholder="0" value={vu[it.k] ?? ''} onChange={e => setVu(p => ({ ...p, [it.k]: e.target.value }))} />
                    </label>
                    <span className="cmp-item-total">{money(r.subtotal)}</span>
                  </div>
                  {abierto && (
                    <>
                      <table className="cmp-lineas">
                        <thead><tr><th></th><th>Proyecto</th><th>Pendiente</th><th>Cantidad a comprar</th><th>Subtotal</th></tr></thead>
                        <tbody>
                          {it.lineas.map(l => {
                            const e = estado(l);
                            return (
                              <tr key={claveLinea(l)} className={e.on ? '' : 'cmp-off'}>
                                <td><input type="checkbox" checked={e.on} onChange={ev => setLinea(l, { on: ev.target.checked })} /></td>
                                <td>
                                  <strong>{proyectoApto(l)}</strong>
                                  <div className="cmp-cliente">COT-{l.consecutivo} · {l.nombre_cliente}{l.enviado_proyecto ? '' : ' · en Ficha FFE'}</div>
                                </td>
                                <td className="cmp-num">{num(pendienteDe(l))} {l.unidad}</td>
                                <td>
                                  <input type="number" min="0" step="any" className="cmp-cant" value={e.cantidad} disabled={!e.on}
                                    onChange={ev => setLinea(l, { cantidad: ev.target.value })} />
                                </td>
                                <td className="cmp-num">{money(cantidadSel(l) * (Number(vu[it.k]) || 0))}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div className="cmp-item-adjuntos">
                        <span>Adjunto de este ítem:</span>
                        {archivosInput(archItem[it.k] || [], lista => setArchItem(p => ({ ...p, [it.k]: lista })))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Datos de la compra / factura */}
          <div className="cmp-form">
            <h3>Datos de la compra</h3>
            <div className="cmp-form-grid">
              <label className="cmp-campo cmp-campo-ancho">
                Proveedor *
                <input type="text" list="cmp-proveedores" value={provTexto} placeholder="Buscar proveedor (escribe o pega)"
                  onChange={e => setProvTexto(e.target.value)} className={provTexto && !provSel ? 'cmp-invalido' : ''} />
                <datalist id="cmp-proveedores">{proveedores.map(p => <option key={p.id} value={p.nombre}>{p.nit ? `NIT ${p.nit}` : ''}</option>)}</datalist>
                {provTexto && !provSel && <small className="cmp-error">Elige un proveedor de la lista</small>}
              </label>
              <label className="cmp-campo">
                Número de factura {form.crear_factura && '*'}
                <input type="text" value={form.numero_factura} placeholder="Ej: FAC-001" onChange={e => setForm({ ...form, numero_factura: e.target.value })} />
              </label>
              <label className="cmp-campo">
                Fecha de compra *
                <input type="date" value={form.fecha_compra} onChange={e => setForm({ ...form, fecha_compra: e.target.value })} />
              </label>
              <label className="cmp-campo">
                Fecha vencimiento {form.crear_factura && '*'}
                <input type="date" value={form.fecha_vencimiento} onChange={e => setForm({ ...form, fecha_vencimiento: e.target.value })} />
              </label>
              <label className="cmp-campo">
                IVA %
                <input type="number" min="0" step="any" value={form.porcentaje_iva} onChange={e => setForm({ ...form, porcentaje_iva: e.target.value })} />
              </label>
              <label className="cmp-campo cmp-campo-ancho">
                Observaciones
                <textarea rows={2} value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })} placeholder="Observaciones adicionales..." />
              </label>
            </div>
            <label className="cmp-check-factura">
              <input type="checkbox" checked={form.crear_factura} onChange={e => setForm({ ...form, crear_factura: e.target.checked })} />
              Crear factura automáticamente en Gestión de Pagos
            </label>
            <div className="cmp-adjuntos-gen">
              <span>Documentos / fotos de toda la compra {form.crear_factura && '*'}</span>
              {archivosInput(archGen, setArchGen)}
            </div>
          </div>
        </div>

        <div className="cmp-modal-footer">
          <div className="cmp-totales">
            <span>{nLineas} línea{nLineas === 1 ? '' : 's'}</span>
            <span>Subtotal <strong>{money(subtotal)}</strong></span>
            <span>IVA <strong>{money(iva)}</strong></span>
            <span className="cmp-total">Total <strong>{money(total)}</strong></span>
          </div>
          <div className="cmp-acciones">
            <button className="ccg-btn-sec" onClick={onClose} disabled={guardando}>Cancelar</button>
            <button className="cmp-btn-prim" onClick={guardar} disabled={guardando || !nLineas}>
              {guardando ? 'Registrando...' : 'Registrar compra'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
